/* eslint-disable camelcase, @typescript-eslint/naming-convention */
/**
 * AI Block Types — Response Protocol Contract
 *
 * Defines all TypeScript interfaces for the block-based response envelope
 * between the LangGraph orchestrator and the frontend chat UI.
 *
 * Property names use snake_case to match the wire protocol from the backend.
 *
 * Source of truth: docs/ai-knowledge/features/chat-widget-response-protocol.md
 *
 * @version 1.0.0
 * @since 2026-02-18
 */
/**
 * Every block type the Support Agent emits. Runtime value, not just a type, so the
 * registry-parity tests enumerate it: a fifth type cannot be added to the union above
 * without a fixture and a renderer registration being demanded here.
 */
export const SUPPORT_AGENT_BLOCK_TYPES = [
    'source_citation',
    'resolution_confirmation',
    'zendesk_ticket',
    'support_handoff_offer'
];
/**
 * Support block types that are a prompt to act, not an answer to rate: a message holding one of
 * these (in ANY state, including the collapsed "Chat started" line, and alongside any other
 * block) carries no copy / thumbs / regenerate bar. A support answer (citations, a ticket card)
 * keeps its bar.
 */
export const SUPPORT_NO_ANSWER_ACTIONS_BLOCK_TYPES = ['support_handoff_offer'];
/**
 * Which backend minted a `file_download` block's URL, and therefore which one
 * can mint a replacement. Two produce downloads today: the orchestrator's own
 * generated-file store, and a Rails-side CSV export job.
 *
 * Declared as a runtime tuple so the union and the list of sources are the same
 * object — a third source added here cannot go untested, because the renderer's
 * tests enumerate this array.
 */
export const DOWNLOAD_SOURCES = ['ai_generated_file', 'rails_csv_export'];
// ---------------------------------------------------------------------------
// SSE Event Types
// ---------------------------------------------------------------------------
/**
 * An undo the orchestrator has just settled server-side (IP-15624).
 *
 * Declared as a const so the event name and the `SSEEventType` member below are
 * the same string: handlers key off this, and a typo is a compile error rather
 * than a handler that is registered under a name the stream never sends.
 */
export const UNDO_SETTLED_SSE_EVENT = 'undo_settled';
